import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  Database,
  Filter,
  Search,
  X,
  ChevronDown,
  CheckSquare,
  Square,
  Eye,
  Copy,
  Check,
  Activity,
  Boxes,
  Lock,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface BlocksViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
  database?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-10 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${
              isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
            }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const BlocksView: React.FC<BlocksViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  database,
}) => {
  const [loading, setLoading] = useState(false);
  const [blocksData, setBlocksData] = useState<any>(null);
  const [qBlocksData, setQBlocksData] = useState<any>(null);
  const [locksData, setLocksData] = useState<any>(null);

  // Table State
  const [activeMetricTab, setActiveMetricTab] = useState<'shared_blks_hit' | 'shared_blks_read' | 'shared_blks_dirtied'>('shared_blks_hit');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const [isDbDropdownOpen, setIsDbDropdownOpen] = useState<boolean>(false);
  const [selectedQueryForModal, setSelectedQueryForModal] = useState<any | null>(null);
  const [copiedQuery, setCopiedQuery] = useState<boolean>(false);

  // Dropdown ref & coords
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 192 });

  // Locks Table State
  const [locksSearchQuery, setLocksSearchQuery] = useState<string>('');

  // Legend Hover States
  const [hoveredHits, setHoveredHits] = useState<string | null>(null);
  const [hoveredReads, setHoveredReads] = useState<string | null>(null);
  const [hoveredDirtied, setHoveredDirtied] = useState<string | null>(null);

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 192),
      });
    }
  }, []);

  const toggleDbDropdown = () => {
    if (!isDbDropdownOpen) {
      updateCoords();
    }
    setIsDbDropdownOpen(!isDbDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDbDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [updateCoords]);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60');

    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) params.client_id = clientId;
    if (database) params.database = database;

    const safeGet = async (url: string, p: any) => {
      try {
        const res = await baseapi.get(url, { params: p });
        return res.data?.data || res.data;
      } catch {
        return null;
      }
    };

    try {
      const [bRes, qBlocksRes, locksRes] = await Promise.all([
        safeGet('/db_monitor_dashboard/graph/per_db/blocks', params),
        safeGet('/db_monitor_dashboard/graph/query_blocks', params),
        safeGet('/db_monitor_dashboard/graph/locks', params),
      ]);

      setBlocksData(bRes);
      setQBlocksData(qBlocksRes);
      setLocksData(locksRes);
    } catch (err) {
      console.warn('BlocksView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId, database]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
        ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const formatSeries = (labels: string[], databases: string[], seriesMap: any, metricKey: string) => {
    if (!labels || labels.length === 0 || !databases) return [];
    const timeLabels = formatTimeLabels(labels);
    return labels.map((_, idx) => {
      const point: any = { time: timeLabels[idx] };
      databases.forEach((db) => {
        const val = seriesMap?.[`${db}:${metricKey}`]?.[idx];
        point[db] = val !== undefined && val !== null ? Number(val.toFixed(2)) : 0;
      });
      return point;
    });
  };


  const hitsFormatted = formatSeries(blocksData?.labels || [], blocksData?.databases || [], blocksData?.series || {}, 'shared_blks_hit');
  const readsFormatted = formatSeries(blocksData?.labels || [], blocksData?.databases || [], blocksData?.series || {}, 'shared_blks_read');
  const dirtiedFormatted = formatSeries(blocksData?.labels || [], blocksData?.databases || [], blocksData?.series || {}, 'shared_blks_dirtied');

  const colors = ['#3b82f6', '#10b981', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4'];
  const hasData = (arr: any[]) => arr && arr.length > 0;

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const getDot = (_hovered?: any, _key?: any, _color?: any, _defaultR?: any) => false;


  // Helper to extract list from query_blocks responses
  const extractQueryList = (res: any): any[] => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    return [];
  };

  // Raw list extracted from single API call
  const rawQueriesList = useMemo(() => {
    return extractQueryList(qBlocksData);
  }, [qBlocksData]);

  // Active query list based on active metric tab
  const activeQueriesList = useMemo(() => {
    const list = [...rawQueriesList];
    if (activeMetricTab === 'shared_blks_hit') {
      return list.sort((a, b) => (b.shared_blks_hit || 0) - (a.shared_blks_hit || 0));
    }
    if (activeMetricTab === 'shared_blks_read') {
      return list.sort((a, b) => (b.shared_blks_read || 0) - (a.shared_blks_read || 0));
    }
    if (activeMetricTab === 'shared_blks_dirtied') {
      return list.sort((a, b) => (b.shared_blks_dirtied || 0) - (a.shared_blks_dirtied || 0));
    }
    return list;
  }, [rawQueriesList, activeMetricTab]);

  // Extract all available databases from query blocks data
  const availableDatabases = useMemo(() => {
    const set = new Set<string>();
    rawQueriesList.forEach((q) => {
      if (q.database) set.add(q.database);
    });
    return Array.from(set).sort();
  }, [rawQueriesList]);

  // Helper to extract list from locks response
  const extractLocksList = (res: any): any[] => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    return [];
  };

  const rawLocksList = useMemo(() => {
    return extractLocksList(locksData);
  }, [locksData]);

  const filteredLocks = useMemo(() => {
    return rawLocksList.filter((l) => {
      if (!locksSearchQuery) return true;
      const q = locksSearchQuery.toLowerCase();
      return (
        (l.database && l.database.toLowerCase().includes(q)) ||
        (l.schema && l.schema.toLowerCase().includes(q)) ||
        (l.table && l.table.toLowerCase().includes(q)) ||
        (l.lock_type && l.lock_type.toLowerCase().includes(q)) ||
        (l.lock_mode && l.lock_mode.toLowerCase().includes(q)) ||
        (l.status && l.status.toLowerCase().includes(q))
      );
    });
  }, [rawLocksList, locksSearchQuery]);

  const toggleDatabaseSelect = (dbName: string) => {
    if (selectedDatabases.includes(dbName)) {
      setSelectedDatabases(selectedDatabases.filter((d) => d !== dbName));
    } else {
      setSelectedDatabases([...selectedDatabases, dbName]);
    }
  };

  const isAllSelected = selectedDatabases.length === 0 || selectedDatabases.length === availableDatabases.length;

  const toggleAllDatabases = () => {
    if (isAllSelected) {
      setSelectedDatabases([]);
    } else {
      setSelectedDatabases([]);
    }
  };

  const buttonLabel = useMemo(() => {
    if (selectedDatabases.length === 0 || selectedDatabases.length === availableDatabases.length) {
      return 'All Databases';
    }
    if (selectedDatabases.length === 1) {
      return selectedDatabases[0];
    }
    return `${selectedDatabases.length} Databases`;
  }, [selectedDatabases, availableDatabases]);

  // Filtered queries for table
  const filteredQueries = useMemo(() => {
    return activeQueriesList.filter((q) => {
      const matchSearch =
        !searchQuery ||
        (q.query && q.query.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (q.database && q.database.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (q.username && q.username.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchDb =
        selectedDatabases.length === 0 ||
        (q.database && selectedDatabases.includes(q.database));

      return matchSearch && matchDb;
    });
  }, [activeQueriesList, searchQuery, selectedDatabases]);

  const handleCopyQuery = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedQuery(true);
    setTimeout(() => setCopiedQuery(false), 2000);
  };

  return (
    <div className="space-y-4 relative">
      {loading && (
        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
        </div>
      )}

      {/* Header */}
      <div>
        <h2 className="text-sm font-bold text-white flex items-center gap-2">
          <div>
            <Boxes className="w-4 h-4 text-orange-500" />
          </div>
          Data Blocks & Buffer I/O
        </h2>
      </div>

      {/* Section 1: Total Shared Blocks per DB */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* Hits */}
        <div>
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Total Shared Block Hits by DB</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(hitsFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hitsFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredHits} onHoverSeries={setHoveredHits} />} />
                  {Object.keys(hitsFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => (
                    <Line
                      key={dbKey}
                      type="monotone"
                      dataKey={dbKey}
                      stroke={colors[i % colors.length]}
                      strokeOpacity={getOpacity(hoveredHits, dbKey)}
                      strokeWidth={hoveredHits === dbKey ? 2.5 : 1.5}
                      dot={getDot()}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No block hit data available</span>
            )}
          </div>
        </div>

        {/* Reads */}
        <div>
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Total Shared Block Read by DB</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(readsFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={readsFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredReads} onHoverSeries={setHoveredReads} />} />
                  {Object.keys(readsFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => (
                    <Line
                      key={dbKey}
                      type="monotone"
                      dataKey={dbKey}
                      stroke={colors[(i + 1) % colors.length]}
                      strokeOpacity={getOpacity(hoveredReads, dbKey)}
                      strokeWidth={hoveredReads === dbKey ? 2.5 : 1.5}
                      dot={getDot()}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No block read data available</span>
            )}
          </div>
        </div>

        {/* Dirtied */}
        <div>
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Total Shared Block Dirtied by DB</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(dirtiedFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dirtiedFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredDirtied} onHoverSeries={setHoveredDirtied} />} />
                  {Object.keys(dirtiedFormatted[0] || {}).filter((k) => k !== 'time').map((dbKey, i) => (
                    <Line
                      key={dbKey}
                      type="monotone"
                      dataKey={dbKey}
                      stroke={colors[(i + 2) % colors.length]}
                      strokeOpacity={getOpacity(hoveredDirtied, dbKey)}
                      strokeWidth={hoveredDirtied === dbKey ? 2.5 : 1.5}
                      dot={getDot()}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No block dirtied data available</span>
            )}
          </div>
        </div>
      </div>

      {/* Section 2: Block I/O by Query (Table View) */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 border-b border-gray-700/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 text-blue-400">
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Block I/O by Query</h3>
              <p className="text-[11px] text-gray-400">Shared block hits, reads, and dirtied per normalized query (pg_stat_statements).</p>
            </div>
          </div>

          {/* Metric Sub-Tabs */}
          <div className="flex items-center gap-1 bg-gray-900/80 p-1 rounded-lg border border-gray-700/60 shrink-0">
            <button
              onClick={() => setActiveMetricTab('shared_blks_hit')}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                activeMetricTab === 'shared_blks_hit'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/60'
              }`}
            >
              Block Hits
            </button>
            <button
              onClick={() => setActiveMetricTab('shared_blks_read')}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                activeMetricTab === 'shared_blks_read'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/60'
              }`}
            >
              Block Reads
            </button>
            <button
              onClick={() => setActiveMetricTab('shared_blks_dirtied')}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                activeMetricTab === 'shared_blks_dirtied'
                  ? 'bg-amber-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/60'
              }`}
            >
              Block Dirtied
            </button>
          </div>
        </div>

        {/* Toolbar: Search & Database Filter */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search by Query / User / DB */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search query, db, user..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-900/90 border border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 w-60 h-8"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Database Multi-Select Dropdown */}
            {availableDatabases.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs text-gray-400 font-medium">Database:</span>
                <button
                  ref={buttonRef}
                  type="button"
                  onClick={toggleDbDropdown}
                  className="flex items-center justify-between gap-2 bg-gray-900/90 hover:bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs font-semibold text-white cursor-pointer transition-colors w-44 shadow-sm h-8"
                >
                  <span className="truncate">{buttonLabel}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${isDbDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isDbDropdownOpen &&
                  createPortal(
                    <div
                      ref={dropdownRef}
                      style={{
                        position: 'absolute',
                        top: `${coords.top}px`,
                        left: `${coords.left}px`,
                        width: `${coords.width}px`,
                        zIndex: 99999,
                      }}
                      className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1.5 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
                    >
                      {/* Select All Option */}
                      <button
                        type="button"
                        onClick={toggleAllDatabases}
                        className="w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors hover:bg-gray-800/80 group border-b border-gray-800 pb-1.5 mb-1"
                      >
                        <div className="flex items-center justify-center shrink-0">
                          {isAllSelected ? (
                            <CheckSquare size={16} className="text-orange-400 fill-orange-500/10" />
                          ) : (
                            <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                          )}
                        </div>
                        <span className="text-gray-200 font-bold">All Databases</span>
                      </button>

                      {/* Available Databases List */}
                      {availableDatabases.map((dbName) => {
                        const isSelected = selectedDatabases.includes(dbName);
                        return (
                          <button
                            key={dbName}
                            type="button"
                            onClick={() => toggleDatabaseSelect(dbName)}
                            className="w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors hover:bg-gray-800/80 group"
                          >
                            <div className="flex items-center justify-center shrink-0">
                              {isSelected || isAllSelected ? (
                                <CheckSquare size={16} className="text-orange-400 fill-orange-500/10" />
                              ) : (
                                <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                              )}
                            </div>
                            <span className="text-gray-200 truncate">{dbName}</span>
                          </button>
                        );
                      })}
                    </div>,
                    document.body
                  )}
              </div>
            )}
          </div>

          <div className="text-[11px] text-gray-400 font-mono">
            Showing <span className="text-white font-bold">{filteredQueries.length}</span> queries
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto custom-scroll rounded-lg border border-gray-700/50">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-900/60 border-b border-gray-700/60 text-[11px] text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 w-28">Database</th>
                <th className="py-2.5 px-3 w-24">User</th>
                <th className="py-2.5 px-3 min-w-[280px]">Query Text</th>
                <th className="py-2.5 px-3 text-right w-20">Calls</th>
                <th className="py-2.5 px-3 text-right w-24">Avg Time</th>
                <th className="py-2.5 px-3 text-right w-24">Total Time</th>
                <th className={`py-2.5 px-3 text-right w-28 ${activeMetricTab === 'shared_blks_hit' ? 'text-blue-400 font-bold bg-blue-500/10' : ''}`}>
                  Shared Hits
                </th>
                <th className={`py-2.5 px-3 text-right w-28 ${activeMetricTab === 'shared_blks_read' ? 'text-purple-400 font-bold bg-purple-500/10' : ''}`}>
                  Shared Reads
                </th>
                <th className={`py-2.5 px-3 text-right w-28 ${activeMetricTab === 'shared_blks_dirtied' ? 'text-amber-400 font-bold bg-amber-500/10' : ''}`}>
                  Shared Dirtied
                </th>
                <th className="py-2.5 px-3 text-right w-24">Cache Hit %</th>
                <th className="py-2.5 px-2 text-center w-12">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30 text-gray-200">
              {filteredQueries && filteredQueries.length > 0 ? (
                filteredQueries.map((q, idx) => {
                  const avg = q.avg_time_ms || 0;
                  const avgStr = avg > 1000 ? `${(avg / 1000).toFixed(2)}s` : `${avg.toFixed(1)}ms`;
                  const total = q.total_time_ms || 0;
                  const totalStr = total > 1000 ? `${(total / 1000).toFixed(2)}s` : `${total.toFixed(0)}ms`;

                  const hitRatio = q.cache_hit_ratio;
                  const ratioColor =
                    hitRatio == null
                      ? 'text-gray-400'
                      : hitRatio >= 99
                      ? 'text-emerald-400'
                      : hitRatio >= 90
                      ? 'text-amber-400'
                      : 'text-red-400';

                  return (
                    <tr key={idx} className="hover:bg-gray-800/60 transition-colors group">
                      <td className="py-2.5 px-3 font-mono text-gray-300">
                        <span className="bg-gray-800 px-2 py-0.5 rounded text-[11px] border border-gray-700/60">
                          {q.database || '-'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-gray-400 truncate max-w-[100px]" title={q.username || ''}>
                        {q.username || '-'}
                      </td>
                      <td
                        onClick={() => setSelectedQueryForModal(q)}
                        className="py-2.5 px-3 font-mono text-blue-300 max-w-md truncate cursor-pointer hover:text-blue-200 hover:underline"
                        title="Click to inspect query"
                      >
                        {q.query}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-300">{(q.calls || 0).toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-200 font-semibold">{avgStr}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-200 font-semibold">{totalStr}</td>
                      <td className={`py-2.5 px-3 text-right font-mono ${activeMetricTab === 'shared_blks_hit' ? 'text-blue-400 font-bold bg-blue-500/5' : 'text-gray-300'}`}>
                        {(q.shared_blks_hit || 0).toLocaleString()}
                      </td>
                      <td className={`py-2.5 px-3 text-right font-mono ${activeMetricTab === 'shared_blks_read' ? 'text-purple-400 font-bold bg-purple-500/5' : 'text-gray-300'}`}>
                        {(q.shared_blks_read || 0).toLocaleString()}
                      </td>
                      <td className={`py-2.5 px-3 text-right font-mono ${activeMetricTab === 'shared_blks_dirtied' ? 'text-amber-400 font-bold bg-amber-500/5' : 'text-gray-300'}`}>
                        {(q.shared_blks_dirtied || 0).toLocaleString()}
                      </td>
                      <td className={`py-2.5 px-3 text-right font-mono font-bold ${ratioColor}`}>
                        {hitRatio != null ? `${Number(hitRatio).toFixed(2)}%` : '-'}
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <button
                          onClick={() => setSelectedQueryForModal(q)}
                          className="p-1 hover:bg-gray-700/60 rounded text-gray-400 hover:text-white transition-colors"
                          title="View Query"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="py-8 text-center text-gray-400 italic">
                    {searchQuery
                      ? `No queries matching "${searchQuery}".`
                      : selectedDatabases.length > 0
                      ? 'No queries found for selected databases.'
                      : 'No query block I/O statistics available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3: Locks */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-700/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 text-red-400">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-white">Active Locks</h3>
              </div>
              <p className="text-[11px] text-gray-400">Lock counts by target object, lock mode, and grant status (pg_locks).</p>
            </div>
          </div>

          {/* Search by Lock / Table / DB */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search table, mode, type, db..."
              value={locksSearchQuery}
              onChange={(e) => setLocksSearchQuery(e.target.value)}
              className="bg-gray-900/90 border border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 w-64 h-8"
            />
            {locksSearchQuery && (
              <button
                onClick={() => setLocksSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Locks Table */}
        <div className="overflow-x-auto custom-scroll rounded-lg border border-gray-700/50">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-900/60 border-b border-gray-700/60 text-[11px] text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 w-32">Database</th>
                <th className="py-2.5 px-3 w-32">Schema</th>
                <th className="py-2.5 px-3 min-w-[140px]">Table / Target</th>
                <th className="py-2.5 px-3 w-32">Lock Type</th>
                <th className="py-2.5 px-3 w-44">Lock Mode</th>
                <th className="py-2.5 px-3 text-center w-24">Status</th>
                <th className="py-2.5 px-3 text-right w-20">Count</th>
                <th className="py-2.5 px-3 text-right w-36">Collected At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30 text-gray-200">
              {filteredLocks && filteredLocks.length > 0 ? (
                filteredLocks.map((l, idx) => {
                  // const isExclusive = (l.lock_mode || '').toLowerCase().includes('exclusive');
                  const isGranted = (l.status || '').toLowerCase() === 'granted';
                  const isWaiting = (l.status || '').toLowerCase() === 'waiting';
                  const isBlocked = (l.status || '').toLowerCase() === 'blocked';

                  const formatCollectedAt = (ts: string) => {
                    if (!ts) return '-';
                    const d = new Date(ts);
                    return isNaN(d.getTime()) ? ts : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                  };

                  return (
                    <tr key={idx} className="hover:bg-gray-800/60 transition-colors">
                      <td className="py-2.5 px-3 font-mono text-gray-300">
                        {l.database ? (
                          <span className="bg-gray-800 px-2 py-0.5 rounded text-[11px] border border-gray-700/60 text-gray-300">
                            {l.database}
                          </span>
                        ) : (
                          <span className="text-gray-500 italic">-</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-gray-400">{l.schema || '-'}</td>
                      <td className="py-2.5 px-3 font-mono text-blue-300 font-semibold">{l.table || '-'}</td>
                      <td className="py-2.5 px-3 font-mono">
                        <span className="bg-gray-800/80 px-2 py-0.5 rounded text-[11px] border border-gray-700 text-gray-300">
                          {l.lock_type || '-'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-mono">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] font-semibold`}
                        >
                          {l.lock_mode || '-'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                            isGranted
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                              : isWaiting
                              ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                              : isBlocked
                              ? 'bg-red-500/10 text-red-400 border-red-500/30'
                              : 'bg-gray-500/10 text-gray-400 border-gray-500/30'
                          }`}
                        >
                          {l.status || '-'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-white">{(l.count || 0).toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400 text-[11px]">
                        {formatCollectedAt(l.collected_at)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-400 italic">
                    {locksSearchQuery ? `No locks matching "${locksSearchQuery}".` : 'No active database locks found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Query Detail Modal */}
      {selectedQueryForModal &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
              {/* Modal Header */}
              <div className="px-5 py-3.5 border-b border-gray-800 flex items-center justify-between bg-gray-900/90">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded bg-blue-500/20 text-blue-400">
                    <Database className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Query Details &amp; Block Statistics</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-gray-400">DB: <span className="text-gray-200 font-mono">{selectedQueryForModal.database || '-'}</span></span>
                      <span className="text-gray-600">•</span>
                      <span className="text-[11px] text-gray-400">User: <span className="text-gray-200 font-mono">{selectedQueryForModal.username || '-'}</span></span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedQueryForModal(null)}
                  className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal Metrics Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-gray-950/40 border-b border-gray-800">
                <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Calls</div>
                  <div className="text-sm font-bold text-white font-mono mt-0.5">
                    {(selectedQueryForModal.calls || 0).toLocaleString()}
                  </div>
                </div>
                <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Avg / Total Time</div>
                  <div className="text-xs font-bold text-white font-mono mt-0.5">
                    {(selectedQueryForModal.avg_time_ms || 0).toFixed(1)}ms / {(selectedQueryForModal.total_time_ms || 0).toLocaleString()}ms
                  </div>
                </div>
                <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                  <div className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold">Shared Hits</div>
                  <div className="text-sm font-bold text-blue-400 font-mono mt-0.5">
                    {(selectedQueryForModal.shared_blks_hit || 0).toLocaleString()}
                  </div>
                </div>
                <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold">Cache Hit Ratio</div>
                  <div className="text-sm font-bold text-emerald-400 font-mono mt-0.5">
                    {selectedQueryForModal.cache_hit_ratio != null ? `${Number(selectedQueryForModal.cache_hit_ratio).toFixed(2)}%` : '-'}
                  </div>
                </div>
              </div>

              {/* Modal Query Body */}
              <div className="p-4 flex-1 overflow-y-auto custom-scroll space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">SQL Statement</span>
                  <button
                    onClick={() => handleCopyQuery(selectedQueryForModal.query)}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-md border border-gray-700 transition-colors font-medium cursor-pointer"
                  >
                    {copiedQuery ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-emerald-400">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                        <span>Copy Query</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className="bg-gray-950 p-3.5 rounded-lg border border-gray-800 text-xs text-blue-300 font-mono whitespace-pre-wrap break-words leading-relaxed max-h-80 overflow-y-auto custom-scroll select-all">
                  {selectedQueryForModal.query || '-'}
                </pre>
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-gray-800 bg-gray-900/90 flex justify-end">
                <button
                  onClick={() => setSelectedQueryForModal(null)}
                  className="px-4 py-1.5 bg-gray-800 hover:bg-gray-700 text-xs text-white font-medium rounded-lg border border-gray-700 transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default BlocksView;
